import { isNative, request } from './native.bridge';
import type { AgentReceipt } from './printer.receipts';
import type { PrinterSettings } from './printer.settings';

// Le contrat de l'agent local (@m-scan/device-kit-agent) : le navigateur ne peut ni ouvrir de socket TCP
// ni parler à une file d'impression, tout passe par ce petit serveur HTTP posé sur le PC de l'imprimante.
// Dans l'application tablette, c'est l'application qui joue l'agent : voir `native` plus bas.
const REQUEST_TIMEOUT_MS = 8_000;
const SCAN_TIMEOUT_MS = 30_000;
const PING_TIMEOUT_MS = 6_000;
const NATIVE_SCAN_TIMEOUT_MS = 60_000;

export interface FoundPrinter {
  id: string;
  name: string;
  address?: string;
  meta?: { port?: number; printerName?: string };
}

// `unreachable` : l'agent lui-même ne répond pas ; `refused` : l'agent répond mais l'imprimante, non.
export class PrinterError extends Error {
  constructor(
    message: string,
    readonly kind: 'unreachable' | 'timeout' | 'refused' = 'refused',
  ) {
    super(message);
  }
}

const PATHS = {
  network: { scan: '/api/scan', connect: '/api/connect', print: '/api/print' },
  usb: {
    scan: '/api/usb/scan',
    connect: '/api/usb/connect',
    print: '/api/usb/print',
  },
} as const;

// Ce qui désigne l'imprimante visée : une adresse IP et un port, ou le nom de la file d'impression.
export const targetOf = (settings: PrinterSettings) =>
  settings.transport === 'network'
    ? { host: settings.host.trim(), port: settings.port }
    : { printerName: settings.printerName.trim() };

const call = async (
  settings: PrinterSettings,
  path: string,
  timeoutMs: number,
  body?: unknown,
) => {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), timeoutMs);
  try {
    const response = await fetch(
      `${settings.agentUrl.trim().replace(/\/$/, '')}${path}`,
      {
        signal: abort.signal,
        ...(body === undefined
          ? {}
          : {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            }),
      },
    );
    const data = (await response.json().catch(() => ({}))) as {
      error?: string;
      devices?: FoundPrinter[];
    };
    if (!response.ok)
      throw new PrinterError(data.error ?? `HTTP ${response.status}`);
    return data;
  } catch (error) {
    if (error instanceof PrinterError) throw error;
    // fetch ne dit pas pourquoi : agent éteint, mauvaise adresse ou page en https contre un agent en http.
    throw new PrinterError(
      abort.signal.aborted
        ? 'L’agent d’impression ne répond pas.'
        : `L’agent d’impression est injoignable (${settings.agentUrl}).`,
      abort.signal.aborted ? 'timeout' : 'unreachable',
    );
  } finally {
    clearTimeout(timer);
  }
};

// Dans l'application tablette les mêmes opérations passent par le pont. Réseau seulement : une tablette n'a pas de file USB.
const nativeCall = async <T>(
  type: string,
  payload: unknown,
  timeoutMs = REQUEST_TIMEOUT_MS,
) => {
  try {
    return await request<T>(type, payload, timeoutMs);
  } catch (error) {
    throw new PrinterError(
      error instanceof Error ? error.message : 'La tablette n’a pas répondu.',
    );
  }
};

const nativeTarget = (settings: PrinterSettings) => {
  if (settings.transport !== 'network')
    throw new PrinterError(
      'L’USB n’est pas disponible sur la tablette : choisissez le réseau.',
    );
  return { ip: settings.host.trim(), port: settings.port };
};

// La recherche couvre le réseau de l'imprimante déjà saisie, sinon celui de la tablette.
const networkOf = (host: string) =>
  /^(\d{1,3}\.\d{1,3}\.\d{1,3})\.\d{1,3}$/.exec(host.trim())?.[1];

// L'agent n'a pas de route de santé : n'importe quelle réponse HTTP (même un 404) prouve qu'il est là et joignable.
export const pingAgent = async (agentUrl: string) => {
  // L'agent est l'application elle-même : si cette page s'affiche, il répond.
  if (isNative()) return;
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), PING_TIMEOUT_MS);
  try {
    await fetch(`${agentUrl.trim().replace(/\/$/, '')}/`, {
      signal: abort.signal,
    });
  } catch {
    throw new PrinterError(
      abort.signal.aborted
        ? 'L’agent d’impression ne répond pas.'
        : `L’agent d’impression est injoignable (${agentUrl}).`,
      abort.signal.aborted ? 'timeout' : 'unreachable',
    );
  } finally {
    clearTimeout(timer);
  }
};

// Cherche les imprimantes que l'agent voit : le réseau local (port 9100) ou les files d'impression USB.
export const scanPrinters = async (
  settings: PrinterSettings,
): Promise<FoundPrinter[]> => {
  if (isNative()) {
    nativeTarget(settings);
    const { printers } = await nativeCall<{ printers: string[] }>(
      'SCAN_PRINTERS',
      { port: settings.port, network: networkOf(settings.host) },
      NATIVE_SCAN_TIMEOUT_MS,
    );
    return printers.map((ip) => ({
      id: ip,
      name: `Imprimante ${ip}`,
      address: ip,
      meta: { port: settings.port },
    }));
  }
  const query =
    settings.transport === 'network' ? `?port=${settings.port}` : '';
  const data = await call(
    settings,
    `${PATHS[settings.transport].scan}${query}`,
    SCAN_TIMEOUT_MS,
  );
  return data.devices ?? [];
};

// Ouvre la connexion que l'agent garde pour les impressions suivantes : sert aussi à vérifier que l'imprimante répond.
export const connectPrinter = async (settings: PrinterSettings) => {
  if (isNative()) {
    await nativeCall('CHECK_PRINTER', nativeTarget(settings));
    return;
  }
  await call(
    settings,
    PATHS[settings.transport].connect,
    REQUEST_TIMEOUT_MS,
    targetOf(settings),
  );
};

export const printReceipt = async (
  settings: PrinterSettings,
  receipt: AgentReceipt,
) => {
  if (isNative()) {
    await nativeCall('PRINT_RECEIPT', {
      ...nativeTarget(settings),
      receipt,
      paperWidth: settings.paperWidth,
    });
    return;
  }
  await call(settings, PATHS[settings.transport].print, REQUEST_TIMEOUT_MS, {
    ...targetOf(settings),
    receipt,
    style: {
      paperWidth: settings.paperWidth,
      topMarginLines: 2,
      bottomMarginLines: 8,
    },
  });
};
